/**
 * @description tabs
 * @author duanjun
 * @date
 */

import React, { ReactChild, ReactElement, Component } from 'react';
import { $document, $window } from '@umu/umu-dom';
import Tab from './tab';
import './tabs.scss';

interface ITabsProps {
    className?: string;
    currentTabIndex?: number;
    hasTabAnchor?: boolean; // 是否为长页面，配合 tabAnchor 以支持滚动定位页面位置
    children?: Array<JSX.Element | null>;
    onTabChange?: (index: number) => void;
}

interface ITabsState {
    tabActiveIndex: number;
}

export default class Tabs extends Component<ITabsProps, ITabsState> {

    constructor(props: ITabsProps) {
        super(props);
        this.state = {
            tabActiveIndex: props.currentTabIndex || 0,
        };

        this.onTabChange = this.onTabChange.bind(this);
        this.renderChild = this.renderChild.bind(this);
        this.setTab = this.setTab.bind(this);
    }

    public getTabActiveArray(length: number) {
        if (!length) {
            return [];
        }

        const activeArray = new Array(length);
        activeArray[this.state.tabActiveIndex] = true;
        return activeArray;
    }

    public setTab(index: number) {
        if (this.state.tabActiveIndex === index) {
            return;
        }
        this.setState({
            tabActiveIndex: index,
        });
    }

    public componentDidMount() {
        this.initTabAnchorListener();
    }

    public componentWillUnmount() {
        this.removeTabAnchorListener();
    }

    public initTabAnchorListener = () => {
        if (!this.props.hasTabAnchor) {
            return;
        }
        $window.addEventListener('scroll', this.scrollEvent);
    }

    public removeTabAnchorListener = () => {
        if (!this.props.hasTabAnchor) {
            return;
        }
        $window.removeEventListener('scroll', this.scrollEvent);
    }

    public scrollEvent = () => {
        const anchors = Array.from($document.querySelectorAll('.tab-anchor'));
        const pageTop = $document.documentElement.scrollTop || $document.body.scrollTop;
        let index = 0;
        anchors.forEach((el, order) => {
            const top = (el as HTMLDivElement).offsetTop;
            if (top <= pageTop) {
                index = order;
            }
        });
        this.setTab(index);
    }

    public scrollToAnchor(index: number) {
        if (!this.props.hasTabAnchor) {
            return;
        }
        const anchors = Array.from($document.querySelectorAll('.tab-anchor'));
        const el = anchors[index] as HTMLDivElement;
        if (!el) {
            return;
        }
        const top = el.offsetTop;
        $document.documentElement.scrollTop = top;
        $document.body.scrollTop = top;
    }

    public onTabChange(event: any) {
        const target = event.currentTarget;
        const index = target.getAttribute('data-index');
        this.scrollToAnchor(index);

        if (this.state.tabActiveIndex === index) {
            return;
        }

        this.setTab(index);
        const onTabChange = this.props.onTabChange;
        if (onTabChange) {
            onTabChange(parseInt(index, 10));
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: ITabsProps) {
        const nextTabIndex = nextProps.currentTabIndex;
        const currentTabIndex = this.props.currentTabIndex;
        if (nextTabIndex !== undefined && nextTabIndex !== currentTabIndex) {
            this.setTab(nextTabIndex);
        }
    }

    public renderChild() {
        let children = this.props.children;
        children = children && children.filter((child) => {
            return !!child;
        });
        const tabActiveArray = this.getTabActiveArray(React.Children.count(children));

        return React.Children.map(children || null, (child: ReactChild | null, key: number) => {
            if (!child) {
                return;
            }
            const element = child as ReactElement<any>;
            if (element && element.type === Tab) {
                return React.cloneElement(element, {
                    onTabChange: this.onTabChange,
                    index: key,
                    active: tabActiveArray[key],
                });
            } else {
                return element;
            }
        });
    }

    public render() {
        const tabsClassName = [
            'tabs',
            (this.props.className || 'theme-default'),
        ].join(' ');
        return (
            <ul className={tabsClassName}>
                {this.renderChild()}
            </ul>
        );
    }
}
